<template>
  <div class="request-view py-2 px-2" :class="{ 'request-ordered': request.result === 'ORDERED' }">
    <v-row dense align="start">
      <v-col cols="12" md="4">
        <div class="body-2 font-weight-medium">{{ partyName || '–' }}</div>
        <div v-if="personName" class="caption grey--text text--darken-1">{{ personName }}</div>
        <div v-else class="caption warning--text">{{ $t('quotations.recipients.person_missing') }}</div>
        <div v-if="details" class="caption grey--text text--darken-1">{{ details }}</div>
        <div v-if="request.source" class="caption text-truncate">
          <a v-if="isUrl" :href="request.source" target="_blank" rel="noopener">{{ request.source }}</a>
          <span v-else>{{ request.source }}</span>
        </div>
        <div v-if="request.note" class="caption request-note">{{ request.note }}</div>
      </v-col>
      <v-col cols="6" md="2" class="body-2">
        <span v-if="request.code" class="font-weight-medium">{{ request.code }}</span>
        <span v-else class="caption grey--text">{{ $t('quotations.recipients.code_on_save') }}</span>
      </v-col>
      <v-col cols="6" md="2" class="body-2" :class="{ 'error--text': deadlineOverdue }">{{ request.deadline ? deadlineText(request.deadline, request.deadlineTime) : '–' }}</v-col>
      <v-col cols="6" md="2" class="body-2">
        <div v-if="request.sentDate" :title="$t('quotations.recipients.sent')">
          <v-icon small color="success">mdi-check</v-icon>
          {{ $formatDateNoTime(request.sentDate) }}
          <span v-if="request.sentVersion && request.sentVersion !== version" class="caption grey--text">
            ({{ $t('quotations.recipients.version_hint', { version: request.sentVersion }) }})
          </span>
        </div>
        <span v-else-if="!persisted || !canSend">–</span>
        <!-- Tarjousdokumentti ja lähetetyksi merkintä tehdään dialogissa -->
        <v-btn v-if="persisted && canSend" x-small text color="primary" class="px-1 ml-n1" @click="$emit('document')">
          {{ $t('quotations.recipients.documents.open') }}
        </v-btn>
      </v-col>
      <v-col cols="4" md="1" class="body-2">
        <v-menu v-if="!readonly && request.sentDate" offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn x-small outlined :color="resultColor" :disabled="busy" v-bind="attrs" v-on="on">
              {{ request.result ? resultText : $t('quotations.recipients.mark_result') }}
              <v-icon x-small right>mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-for="o in resultOptions" :key="o.value || 'none'" :disabled="busy" @click="$emit('result', o.value)">
              <v-list-item-title>{{ o.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <span v-else>{{ resultText }}</span>
        <div v-if="request.result && request.resultReason" class="caption grey--text">{{ request.resultReason }}</div>
      </v-col>
      <v-col cols="2" md="1" class="d-flex justify-end mt-n1">
        <quotation-icon-button v-if="!readonly" icon="mdi-pencil" :tooltip="$t('quotations.recipients.edit')" :disabled="busy" @click="$emit('edit')" />
      </v-col>
      <v-col v-if="(request.dates || []).length > 0" cols="12" md="8" offset-md="4" class="pt-0">
        <quotation-request-dates :dates="request.dates" :readonly="readonly || !persisted" :busy="busy" @change="v => $emit('dates', v)" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { deadlinePassed, deadlineText } from '@/helpers/quotationDeadline';
import QuotationIconButton from './QuotationIconButton';
import QuotationRequestDates from './QuotationRequestDates';

// Tarjouspyyntö luettavana listan rivinä. Tiedot muokataan kynästä avautuvassa muokkaimessa.
export default {
  name: 'QuotationRequestView',
  components: { QuotationIconButton, QuotationRequestDates },
  props: {
    request: { type: Object, required: true },
    partyName: { type: String, default: '' },
    personName: { type: String, default: '' },
    version: { type: Number, default: 1 },
    // Tallennettu pyyntö: numero ja lähetys ovat käytössä
    persisted: { type: Boolean, default: false },
    // Tarjouspyynnöstä ei vielä tehdä tarjousdokumenttia
    canSend: { type: Boolean, default: true },
    readonly: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    resultOptions: { type: Array, default: () => [] },
  },
  computed: {
    details() {
      const r = this.request;
      return r.receivedDate ? `${this.$t('quotations.recipients.received')} ${this.$formatDateNoTime(r.receivedDate)}` : '';
    },
    isUrl() {
      return /^https?:\/\//i.test(this.request.source || '');
    },
    deadlineOverdue() {
      const r = this.request;
      return !!r.deadline && !r.sentDate && !r.result && deadlinePassed(r.deadline, r.deadlineTime);
    },
    resultColor() {
      return { ORDERED: 'success', REJECTED: 'error' }[this.request.result] || 'primary';
    },
    resultText() {
      return this.request.result ? this.$t(`quotations.recipients.results.${this.request.result}`) : '–';
    },
  },
  methods: { deadlineText },
};
</script>

<style scoped>
.request-note {
  white-space: pre-line;
}
/* Tilaajan valitsema tarjous */
.request-ordered {
  background-color: var(--v-success-lighten5);
}
</style>
